// Changing the password from the account page (scenario A6, B-244).
//
// The current password first, because a session alone is not enough to
// change the one thing that gets you back in. Then the new one, held to the
// sign-up rule. Then, when anything else is signed in, whether to sign it
// out: ticked by default, for B-58's reason, and the sentence under the box
// says what each answer will do before the button is pressed.

import { useState } from 'react';
import { PASSWORD_MIN_LENGTH, type PasswordChanged } from '@fsp/domain';
import { messageOf } from '../../data/api';
import { changePassword } from '../../data/devices';
import { Button, Checkbox, Dialog, Line, PasswordField, Stack, tell } from '../../kit/ui';
import { newPasswordRefusal, PASSWORD_RULE } from './passwordRule';

function devices(n: number): string {
  return n === 1 ? 'device' : `${n} devices`;
}

export function ChangePasswordSheet({
  opened,
  onClose,
  others,
  onChanged,
}: {
  opened: boolean;
  onClose: () => void;
  /**
   * Other devices signed in, from the list the account page holds, or null
   * while it is not in hand. For the box's label only: what was signed out
   * is the server's count, because this list can be a minute old.
   */
  others: number | null;
  /** What the change did, as the server counted it. */
  onChanged: (changed: PasswordChanged) => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [signOutOthers, setSignOutOthers] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<{ current?: string; next?: string }>({});

  const close = () => {
    setCurrent('');
    setNext('');
    setSignOutOthers(true);
    setErrors({});
    onClose();
  };

  const submit = () => {
    // Said under the field before anything is sent; the server holds the
    // same rule and says the same words.
    const refusal = newPasswordRefusal(next);
    if (refusal !== null) return setErrors({ next: refusal });
    setBusy(true);
    setErrors({});
    void (async () => {
      const reply = await changePassword({ current, next, signOutOthers });
      setBusy(false);
      if (reply.status === 'ok') {
        const gone = reply.data.signedOut;
        close();
        onChanged(reply.data);
        tell(
          gone === 0
            ? 'Password changed.'
            : `Password changed. Your other ${devices(gone)} ${gone === 1 ? 'is' : 'are'} signed out.`,
        );
        return;
      }
      // A refusal about a field is said under that field (`details.issues`):
      // a wrong current password, a new one too short or the same as before.
      const issues = reply.status === 'refused' ? (reply.details?.issues ?? []) : [];
      const onField = {
        current: issues.find((i) => i.path === 'current')?.message,
        next: issues.find((i) => i.path === 'next')?.message,
      };
      if (onField.current !== undefined || onField.next !== undefined) {
        setErrors(onField);
        return;
      }
      tell(
        messageOf(
          reply,
          'Your password did not change.',
          'Could not reach the site. Your password did not change.',
        ),
        'bad',
      );
    })();
  };

  const ready = current.length > 0 && next.length >= PASSWORD_MIN_LENGTH && !busy;

  return (
    <Dialog opened={opened} onClose={close} title="Change your password">
      <Stack gap={3}>
        <PasswordField
          label="Current password"
          value={current}
          onChange={(v) => {
            setCurrent(v);
            setErrors((e) => ({ ...e, current: undefined }));
          }}
          autoComplete="current-password"
          error={errors.current}
        />
        <PasswordField
          label="New password"
          description={PASSWORD_RULE}
          value={next}
          onChange={(v) => {
            setNext(v);
            setErrors((e) => ({ ...e, next: undefined }));
          }}
          autoComplete="new-password"
          error={errors.next}
        />
        {others === 0 ? null : (
          <Checkbox
            label={
              others === null ? 'Sign out my other devices' : `Sign out my other ${devices(others)}`
            }
            description={
              signOutOthers
                ? 'They will need the new password to sign in again. This device stays signed in.'
                : 'They stay signed in.'
            }
            on={signOutOthers}
            onChange={setSignOutOthers}
          />
        )}
        <Line end>
          <Button variant="quiet" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ready} onClick={submit}>
            Change password
          </Button>
        </Line>
      </Stack>
    </Dialog>
  );
}
